"use client";

import { useEffect, useRef } from "react";

// The homepage demo, replayed for the investors page.
//
// It deliberately does NOT reuse HeroDemoAnimator: that one waits on
// `body.cin-ready` from the cinematic intro and drives the DOM through global
// ids defined in Hero.tsx. Neither exists here — PageShell has no intro — so it
// would sit through its own 4.3s fallback before playing, halfway down a page.
//
// This version scopes every lookup to its own subtree and triggers on scroll-in,
// which is the right cue for a mid-page band. The markup reuses the hero's class
// names so it inherits the same styling from globals.css.
//
// SSR bakes the FINAL state (score 72, bars filled, every reveal shown). With JS
// off, or with reduced motion, the panel is complete and correct — the animation
// is only ever a transition into a state that is already there.

const SCORE = 72;

export default function ProductDemo() {
  const root = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = root.current;
    if (!el) return;

    let reduce = false;
    try {
      reduce = !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    } catch {}
    if (reduce) return;

    const score = el.querySelector<HTMLElement>("[data-demo-score]");
    const bars = Array.from(el.querySelectorAll<HTMLElement>(".demo-bar-fill"));
    const reveals = Array.from(el.querySelectorAll<HTMLElement>("[data-demo-reveal]"));
    if (!score) return;

    // Reset to the pre-animation state while the section is still off-screen,
    // so the viewer never sees the final value snap back to zero.
    score.textContent = "0";
    bars.forEach((b) => {
      b.style.width = "0";
    });
    reveals.forEach((r) => r.classList.remove("show"));

    const timers: number[] = [];
    let started = false;

    const play = () => {
      if (started) return;
      started = true;

      let c = 0;
      const counter = window.setInterval(() => {
        c += 2;
        if (c >= SCORE) {
          score.textContent = String(SCORE);
          window.clearInterval(counter);
          return;
        }
        score.textContent = String(c);
      }, 22);
      timers.push(counter);

      const at = (ms: number, fn: () => void) => {
        timers.push(window.setTimeout(fn, ms));
      };

      at(500, () => bars.forEach((b) => { b.style.width = b.dataset.w || ""; }));
      // Reveals are ordered in the markup, so staggering by index keeps the
      // sequence in step with reading order without hard-coding ids.
      reveals.forEach((r, i) => at(700 + i * 340, () => r.classList.add("show")));
    };

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          io.unobserve(entry.target);
          play();
        }
      },
      { threshold: 0.25 },
    );

    io.observe(el);

    return () => {
      io.disconnect();
      timers.forEach((id) => {
        window.clearTimeout(id);
        window.clearInterval(id);
      });
    };
  }, []);

  return (
    <div
      ref={root}
      aria-label="Illustrative contract analysis sample: a vendor agreement scored 72 out of 100, showing three flagged issues with a suggested fix and cited authorities"
      className="inv-demo"
      role="img"
    >
      <div className="hero-r">
        <div className="demo-topbar">
          <div className="demo-dots">
            <div className="ddot" style={{ background: "#FF5F57" }} />
            <div className="ddot" style={{ background: "#FFBD2E" }} />
            <div className="ddot" style={{ background: "#28CA41" }} />
          </div>
          <div className="demo-status">
            <div className="demo-blink" />
            LexVio.ai — Illustrative example
          </div>
        </div>

        <div className="demo-body">
          <div className="demo-contract">
            <div className="demo-contract-title">VendorAgreement_TechCorp.pdf</div>
            <div className="demo-scanline" />
            <div className="demo-text">
              <p>
                <strong style={{ color: "rgba(251,250,246,.95)" }}>14. INDEMNIFICATION</strong>
              </p>
              <p>
                The{" "}
                <span className="hl-r">
                  Vendor shall indemnify, defend, and hold harmless the Company from any and all
                  claims, damages, losses, expenses, and legal fees arising out of or resulting from
                  Vendor&apos;s performance,
                </span>{" "}
                without limitation of any kind.
              </p>
              <p>
                <strong style={{ color: "rgba(251,250,246,.95)" }}>8. INTELLECTUAL PROPERTY</strong>
              </p>
              <p>
                All work product and{" "}
                <span className="hl-a">
                  developments created by Vendor, including all pre-existing intellectual property
                  used in the work,
                </span>{" "}
                shall be assigned to Company in perpetuity.
              </p>
              <p>
                <strong style={{ color: "rgba(251,250,246,.95)" }}>21. TERMINATION</strong>
              </p>
              <p>
                Either party may terminate this Agreement upon{" "}
                <span className="hl-a">30 days written notice, without cause.</span>
              </p>
            </div>
            <div data-demo-reveal aria-label="Document scan summary" className="demo-doc-meta show">
              <span className="demo-doc-meta-chip">
                <b>14</b>/14 clauses
              </span>
              <span className="demo-doc-meta-chip">
                <b>7</b> pages
              </span>
              <span className="demo-doc-meta-chip demo-doc-meta-time">
                scanned in <b>1.8s</b>
              </span>
            </div>
          </div>

          <div className="demo-panel">
            <div className="demo-panel-title">Legal Health Score</div>
            <div className="demo-score-wrap">
              <div data-demo-score className="demo-score-num">
                {SCORE}
              </div>
              <div className="demo-score-sub">/100</div>
            </div>

            <div className="demo-bar-row">
              <span className="demo-bar-lbl">Liability</span>
              <div className="demo-bar-track">
                <div className="demo-bar-fill" data-w="82%" style={{ background: "var(--red)", width: "82%" }} />
              </div>
            </div>
            <div className="demo-bar-row">
              <span className="demo-bar-lbl">IP protection</span>
              <div className="demo-bar-track">
                <div className="demo-bar-fill" data-w="59%" style={{ background: "var(--amber)", width: "59%" }} />
              </div>
            </div>
            <div className="demo-bar-row">
              <span className="demo-bar-lbl">Governing law</span>
              <div className="demo-bar-track">
                <div className="demo-bar-fill" data-w="90%" style={{ background: "var(--green)", width: "90%" }} />
              </div>
            </div>

            <div className="demo-divider" />

            <div data-demo-reveal className="risk-tag show">
              <div className="risk-dot" style={{ background: "var(--red)" }} />
              <div>
                <div className="risk-text">§14 Liability cap absent</div>
              </div>
              <div className="risk-badge rb-r">CRITICAL</div>
            </div>
            <div data-demo-reveal className="risk-tag show">
              <div className="risk-dot" style={{ background: "var(--amber)" }} />
              <div>
                <div className="risk-text">§8 IP overbroad</div>
              </div>
              <div className="risk-badge rb-a">REVIEW</div>
            </div>
            <div data-demo-reveal className="risk-tag show">
              <div className="risk-dot" style={{ background: "var(--amber)" }} />
              <div>
                <div className="risk-text">§21 Termination at-will</div>
              </div>
              <div className="risk-badge rb-a">REVIEW</div>
            </div>

            <div data-demo-reveal className="demo-fix show">
              ✓ Fix: Cap liability at 12-month contract value with explicit carve-outs
            </div>
            <div data-demo-reveal aria-label="Cited authorities" className="demo-cite show">
              <div className="demo-cite-lbl">Cited authorities</div>
              <div className="demo-cite-chip">
                <span className="demo-cite-k">Statute</span>Unfair Contract Terms Act 1977 s.2
              </div>
              <div className="demo-cite-chip">
                <span className="demo-cite-k">Case</span>Photo Production v Securicor [1980] AC 827
              </div>
            </div>
          </div>
        </div>

        <div className="demo-footer">
          <span className="demo-footer-text">Illustrative sample · Run your own via the free scanner</span>
          <a href={`/sign-up`}>
            Try free →
          </a>
        </div>
      </div>
    </div>
  );
}
